import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Plus, Edit, Trash2, Upload, Star, Eye, Images, ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TranslatableInput } from "./TranslatableInput";
import { useLocalized } from "@/hooks/useLocalized";

import { LocalizedPortfolioAlbum, LocalizedPortfolioMedia, LocalizedContent } from "@/types";

type PortfolioCategory = 'portrait' | 'wedding' | 'event' | 'commercial' | 'headshots' | 'family' | 'lifestyle' | 'fashion' | 'product';

type PortfolioAlbum = LocalizedPortfolioAlbum;

const CATEGORIES: PortfolioCategory[] = [
  'portrait', 'wedding', 'event', 'commercial', 'headshots',
  'family', 'lifestyle', 'fashion', 'product'
];

export function PortfolioManager() {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [albums, setAlbums] = useState<PortfolioAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAlbum, setEditingAlbum] = useState<PortfolioAlbum | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<PortfolioAlbum | null>(null);
  const [albumMedia, setAlbumMedia] = useState<LocalizedPortfolioMedia[]>([]);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  const [formData, setFormData] = useState<{
    title: LocalizedContent;
    description: LocalizedContent;
    category: PortfolioCategory;
    is_featured: boolean;
    sort_order: number;
  }>({
    title: '',
    description: '',
    category: 'portrait',
    is_featured: false,
    sort_order: 0
  });

  const fetchAlbums = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio_albums')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setAlbums(data || []);
    } catch (error) {
      console.error('Error fetching albums:', error);
      toast({
        title: "Error",
        description: "Failed to fetch portfolio albums",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingAlbum) {
        const { error } = await supabase
          .from('portfolio_albums')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update(formData as any)
          .eq('id', editingAlbum.id);

        if (error) throw error;
        toast({ title: "Album updated successfully!" });
      } else {
        const { data: profile } = await supabase
          .from('photographer_profiles')
          .select('id')
          .limit(1)
          .single();

        const { error } = await supabase
          .from('portfolio_albums')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .insert([{ ...formData, photographer_id: profile?.id }] as any);

        if (error) throw error;
        toast({ title: "Album created successfully!" });
      }

      fetchAlbums();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving album:', error);
      toast({
        title: "Error",
        description: "Failed to save album",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (album: PortfolioAlbum) => {
    setEditingAlbum(album);
    setFormData({
      title: album.title,
      description: album.description || '',
      category: (album.category as PortfolioCategory) || 'portrait',
      is_featured: album.is_featured,
      sort_order: album.sort_order
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this album?')) return;

    try {
      const { error } = await supabase
        .from('portfolio_albums')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Album deleted successfully!" });
      fetchAlbums();
    } catch (error) {
      console.error('Error deleting album:', error);
      toast({
        title: "Error",
        description: "Failed to delete album",
        variant: "destructive",
      });
    }
  };

  const fetchAlbumMedia = useCallback(async (albumId: string) => {
    try {
      const { data, error } = await supabase
        .from('portfolio_media')
        .select('*')
        .eq('album_id', albumId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setAlbumMedia(data || []);
    } catch (error) {
      console.error('Error fetching album media:', error);
      toast({
        title: "Error",
        description: "Failed to fetch album images",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, albumId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${albumId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('portfolio')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('portfolio')
        .getPublicUrl(fileName);

      // Update album cover image
      const { error } = await supabase
        .from('portfolio_albums')
        .update({ cover_image_url: publicUrl })
        .eq('id', albumId);

      if (error) throw error;
      toast({ title: "Cover image updated successfully!" });
      fetchAlbums();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, albumId: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${albumId}/${Date.now()}-${Math.random()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('portfolio')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('portfolio')
          .getPublicUrl(fileName);

        // Get current max sort order
        const { data: maxData } = await supabase
          .from('portfolio_media')
          .select('sort_order')
          .eq('album_id', albumId)
          .order('sort_order', { ascending: false })
          .limit(1);

        const maxSortOrder = maxData?.[0]?.sort_order || 0;

        // Add to portfolio_media
        const { error: mediaError } = await supabase
          .from('portfolio_media')
          .insert({
            album_id: albumId,
            media_url: publicUrl,
            media_type: 'image',
            sort_order: maxSortOrder + 1
          });

        if (mediaError) throw mediaError;
      });

      await Promise.all(uploadPromises);
      toast({ title: `${files.length} image(s) uploaded successfully!` });
      fetchAlbumMedia(albumId);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaId: string, mediaUrl: string) => {
    if (!confirm('Delete this image?')) return;

    try {
      // Extract file path from URL
      const urlParts = mediaUrl.split('/portfolio/');
      if (urlParts[1]) {
        await supabase.storage.from('portfolio').remove([urlParts[1]]);
      }

      const { error } = await supabase
        .from('portfolio_media')
        .delete()
        .eq('id', mediaId);

      if (error) throw error;
      toast({ title: "Image deleted successfully!" });
      if (selectedAlbum) {
        fetchAlbumMedia(selectedAlbum.id);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    }
  };

  const handleMoveMedia = async (mediaId: string, direction: 'left' | 'right') => {
    if (!selectedAlbum) return;

    const currentIndex = albumMedia.findIndex(m => m.id === mediaId);
    if (currentIndex === -1) return;

    if (direction === 'left' && currentIndex === 0) return;
    if (direction === 'right' && currentIndex === albumMedia.length - 1) return;

    const swapIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    const currentMedia = albumMedia[currentIndex];
    const swapMedia = albumMedia[swapIndex];

    try {
      // Swap sort_order values in database
      await supabase
        .from('portfolio_media')
        .update({ sort_order: swapMedia.sort_order })
        .eq('id', currentMedia.id);

      await supabase
        .from('portfolio_media')
        .update({ sort_order: currentMedia.sort_order })
        .eq('id', swapMedia.id);

      toast({ title: "Image reordered!" });
      fetchAlbumMedia(selectedAlbum.id);
    } catch (error) {
      console.error('Error reordering media:', error);
      toast({
        title: "Error",
        description: "Failed to reorder image",
        variant: "destructive",
      });
    }
  };

  const openMediaDialog = (album: PortfolioAlbum) => {
    setSelectedAlbum(album);
    fetchAlbumMedia(album.id);
    setIsMediaDialogOpen(true);
  };

  const toggleFeatured = async (albumId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('portfolio_albums')
        .update({ is_featured: !currentStatus })
        .eq('id', albumId);

      if (error) throw error;
      toast({
        title: !currentStatus ? "Album featured!" : "Album unfeatured!"
      });
      fetchAlbums();
    } catch (error) {
      console.error('Error updating featured status:', error);
      toast({
        title: "Error",
        description: "Failed to update featured status",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'portrait',
      is_featured: false,
      sort_order: 0
    });
    setEditingAlbum(null);
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading portfolio...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Portfolio Management</h2>
          <p className="text-muted-foreground">Manage your photography portfolio albums</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Album
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingAlbum ? 'Edit Album' : 'Add New Album'}
              </DialogTitle>
              <DialogDescription>
                Create or modify your portfolio albums
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <TranslatableInput
                  label="Album Title"
                  value={formData.title}
                  onChange={(val) => setFormData(prev => ({ ...prev, title: val }))}
                />
              </div>

              <div className="space-y-2">
                <TranslatableInput
                  label="Description"
                  component="textarea"
                  value={formData.description}
                  onChange={(val) => setFormData(prev => ({ ...prev, description: val }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">{t('admin.portfolio.categoryLabel', 'Category')}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value: PortfolioCategory) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin.portfolio.selectCategory', 'Select category')} />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {t(`category.${cat}`, cat.charAt(0).toUpperCase() + cat.slice(1))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sortOrder">Sort Order</Label>
                  <Input
                    id="sortOrder"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="featured">Featured Album</Label>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingAlbum ? 'Update Album' : 'Create Album'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {albums.map((album) => (
          <Card key={album.id} className="relative overflow-hidden">
            <div className="relative h-48">
              {album.cover_image_url ? (
                <img
                  src={album.cover_image_url}
                  alt={getLocalized(album.title)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Eye className="h-12 w-12 text-muted-foreground" />
                </div>
              )}

              {album.is_featured && (
                <Badge className="absolute top-2 left-2 bg-yellow-500 text-yellow-900">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}

              <div className="absolute top-2 right-2 flex gap-1">
                <Button
                  size="sm"
                  variant={album.is_featured ? "default" : "secondary"}
                  className={album.is_featured ? "bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600" : "bg-white/80 hover:bg-white"}
                  onClick={() => toggleFeatured(album.id, album.is_featured)}
                  title={album.is_featured ? "Unfeature album" : "Feature album"}
                >
                  <Star className={`h-4 w-4 ${album.is_featured ? "fill-current" : ""}`} />
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleEdit(album)}>
                  <Edit className="h-4 w-4" />
                </Button>
                {hasPermission('delete_portfolio') && (
                  <Button size="sm" variant="secondary" onClick={() => handleDelete(album.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <CardHeader>
              <CardTitle className="text-lg">{getLocalized(album.title)}</CardTitle>
              {album.category && (
                <Badge variant="outline">
                  {album.category.charAt(0).toUpperCase() + album.category.slice(1)}
                </Badge>
              )}
            </CardHeader>

            <CardContent>
              <CardDescription className="mb-3 line-clamp-2">
                {getLocalized(album.description)}
              </CardDescription>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  disabled={uploading}
                  onClick={() => document.getElementById(`file-${album.id}`)?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Cover
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  onClick={() => openMediaDialog(album)}
                >
                  <Images className="h-4 w-4 mr-1" />
                  Images
                </Button>
              </div>
              <input
                id={`file-${album.id}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, album.id)}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {albums.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground mb-4">No portfolio albums found</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Album
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Media Management Dialog */}
      <Dialog open={isMediaDialogOpen} onOpenChange={setIsMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Album Images - {selectedAlbum?.title && getLocalized(selectedAlbum.title)}</DialogTitle>
            <DialogDescription>
              Upload and manage multiple images for this album
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Button
                onClick={() => document.getElementById('multi-upload')?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Images'}
              </Button>
              <input
                id="multi-upload"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => selectedAlbum && handleMultipleImageUpload(e, selectedAlbum.id)}
              />
            </div>

            {albumMedia.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No images in this album yet. Upload some to get started!
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {albumMedia.map((media, idx) => (
                  <div key={media.id} className="relative group">
                    <img
                      src={media.media_url}
                      alt={media.caption ? getLocalized(media.caption) : 'Album image'}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded-lg">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8"
                        disabled={idx === 0}
                        onClick={() => handleMoveMedia(media.id, 'left')}
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                      {hasPermission('delete_portfolio') && (
                        <Button
                          size="icon"
                          variant="destructive"
                          className="h-8 w-8"
                          onClick={() => handleDeleteMedia(media.id, media.media_url)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8"
                        disabled={idx === albumMedia.length - 1}
                        onClick={() => handleMoveMedia(media.id, 'right')}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}